import {
  declaredAssetType,
  declaredTypeRefusal,
  PORTAL_ASSET_MAX_FILES_PER_REQUEST,
  sizeRefusal,
} from './allowlist.js';
import type { DetectedType } from './detect.js';
import type { PortalAssetMediaType } from '../../types/portalAsset.js';

// PORTAL ASSETS — the upload screen's plan and the resumable protocol's
// arithmetic (spec §4.3, §4.5). PURE: the browser driver in
// src/modules/portalAssets/resumableUpload.ts does the I/O.
//
// ⚠️ The CHECKS HERE ARE A COURTESY. The server re-runs the same allowlist and
// caps (it imports the same allowlist.ts), and the worker decides from the
// BYTES. Refusing early only saves the person a wasted upload and names the
// file while they are still looking at it.

export type ChosenFile = { name: string; type: string; size: number };

export type PlannedFile =
  | { index: number; filename: string; accepted: true; contentType: string; sizeBytes: number }
  | { index: number; filename: string; accepted: false; reason: string };

export function planUploadFiles(files: readonly ChosenFile[]): PlannedFile[] {
  return files.map((f, index) => {
    const declared = declaredAssetType(f.name, f.type);
    if (!declared) return { index, filename: f.name, accepted: false, reason: declaredTypeRefusal(f.name) };
    if (!(f.size > 0)) return { index, filename: f.name, accepted: false, reason: `“${f.name}” is empty.` };
    if (f.size > declared.maxBytes) {
      return { index, filename: f.name, accepted: false, reason: sizeRefusal(f.name, declared.maxBytes) };
    }
    // ⚠️ The ORIGINAL declared type goes to the server, not the resolved one: the
    // server resolves it again with the same function, and an empty HEIC type is
    // part of what it needs to see.
    return { index, filename: f.name, accepted: true, contentType: f.type, sizeBytes: f.size };
  });
}

/**
 * The browser-side byte check: does what the file IS contradict what it was named?
 * PURE — the component reads the bytes, this decides. Returns the refusal sentence, or null to keep.
 *
 * ⚠️ SKIP ONLY ON A CLEAR CONTRADICTION: detection SUCCEEDED and disagrees on photo-vs-video. An
 * unrecognised file (`detected === null`) is KEPT and uploads as before — refusing on a negative is
 * a different, riskier rule, and the worker already fails it with a record and a reason. The
 * detector is the SAME code the worker runs (./detect.ts), so the two cannot disagree.
 * ⚠️ No size floor: a tiny file is not evidence of junk, the bytes are.
 */
export function byteContradictionRefusal(
  filename: string,
  declared: PortalAssetMediaType,
  detected: DetectedType | null,
): string | null {
  if (detected === null || detected.mediaType === declared) return null;
  return declared === 'video'
    ? `“${filename}” is a photo saved with a video name, so it was skipped.`
    : `“${filename}” is a video saved with a photo name, so it was skipped.`;
}

/** Split into requests the server will take (≤ PORTAL_ASSET_MAX_FILES_PER_REQUEST each). */
export function uploadBatches<T>(items: readonly T[], size = PORTAL_ASSET_MAX_FILES_PER_REQUEST): T[][] {
  if (!Number.isInteger(size) || size < 1) throw new Error('uploadBatches: invalid size');
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

// ── The GCS resumable protocol ───────────────────────────────────────────────

/** 8 MiB. ⚠️ Every chunk but the last MUST be a multiple of 256 KiB (GCS). */
export const RESUMABLE_CHUNK_BYTES = 8 * 1024 * 1024;
export const RESUMABLE_GRANULE = 256 * 1024;
/** How many files upload at once (spec §4.3; Q9 — tune from real uploads). */
export const UPLOAD_CONCURRENCY = 4;

/** `Content-Range` for the chunk [start, endExclusive) of a `total`-byte file. */
export function chunkContentRange(start: number, endExclusive: number, total: number): string {
  if (!(start >= 0 && endExclusive > start && endExclusive <= total)) {
    throw new Error(`chunkContentRange: bad range ${start}-${endExclusive}/${total}`);
  }
  return `bytes ${start}-${endExclusive - 1}/${total}`;
}

/** The zero-length status query: "how much of this session do you have?" */
export function statusQueryContentRange(total: number): string {
  return `bytes */${total}`;
}

/**
 * The next byte to send, from a 308's `Range` header. GCS answers
 * `bytes=0-N` meaning 0..N are stored, so the next offset is N+1; NO header
 * means nothing is stored yet (offset 0). ⚠️ Anything unreadable throws — a
 * guessed offset would either re-send bytes or, worse, skip them, and a file
 * with a hole in it is a corrupt original nobody would notice until a client
 * downloaded it.
 */
export function nextOffsetFromRange(range: string | null): number {
  if (range === null || range.trim() === '') return 0;
  const m = /^bytes=0-(\d+)$/.exec(range.trim());
  if (!m) throw new Error(`Unreadable Range header: ${JSON.stringify(range)}`);
  return Number(m[1]) + 1;
}

/** The end (exclusive) of the chunk that starts at `offset`. */
export function chunkEnd(offset: number, total: number, chunkBytes = RESUMABLE_CHUNK_BYTES): number {
  if (chunkBytes % RESUMABLE_GRANULE !== 0) throw new Error('chunk size must be a multiple of 256 KiB');
  return Math.min(offset + chunkBytes, total);
}
